import { useRef, useState, useMemo, useCallback, useEffect } from 'react'
import { AgGridReact } from 'ag-grid-react'
import clsx from 'clsx'
import { useMutation, useLazyQuery, gql } from '@apollo/client'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import { format } from 'date-fns'

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'

import { ReactComponent as CheckIcon } from '../assets/icons/check-icon.svg'
import { ReactComponent as CrossIcon } from '../assets/icons/cross-icon.svg'

import {
  sortStringFunc,
  sortNumberFunc,
  makeCSV,
  generatePointsReport,
  generateReport,
} from '../utils'
import { ROLE_FACILITATOR, ROLE_PARTICIPANT } from '../constants'
import {
  GET_INVITES,
  GET_USERS,
  GET_POINTS_UPDATE_TRANSACTION_REPORT,
  UPDATE_USER_ROLE,
  CREATE_INVITE_MUTATION,
  SEND_INVITE_EMAIL_MUTATION,
  INVITE_SUBJECT,
  generateInviteBody,
} from '../services/admin'
import { ConfirmDialog, AdjustPointsDialog } from '../components'

const GET_AGENDA_ITEMS = gql`
  query GetAgendaItems {
    agendaItems(first: 50) {
      edges {
        node {
          order
          postId
          title
          detail {
            knowledgeCheck {
              questions {
                point
              }
            }
          }
        }
      }
    }
  }
`
const GET_LEARNING_LOUNGE_ITEMS = gql`
  query GetLearningLoungeItems {
    learningLoungeItems(first: 200) {
      edges {
        node {
          order
          postId
          title
          detail {
            questions {
              point
            }
          }
        }
      }
    }
  }
`
export const UPDATE_USER_POINTS_MUTATION = gql`
  mutation UpdateUserPoints($userId: Int!, $operation: String!, $bonus: Int!) {
    updateUserPoints(
      input: { userId: $userId, operation: $operation, bonus: $bonus }
    ) {
      userId
      operation
      bonus
      pointsBefore
      pointsAfter
    }
  }
`
const REMOVE_USER = gql`
  mutation RemoveUser($clientMutationId: String, $username: String!) {
    removeUser(
      input: { clientMutationId: $clientMutationId, username: $username }
    ) {
      username
      invite_post_removed
      user_removed
    }
  }
`

const AdminViewRegistrants = () => {
  const [rows, setRows] = useState([])
  const [cdChangeUserRoleOpen, setCdChangeUserRoleOpen] = useState(false)
  const [cdInviteUserOpen, setCdInviteUserOpen] = useState(false)
  const [cdInviteUserText, setCdInviteUserText] = useState('')
  const [removingUsername, setRemovingUsername] = useState('')
  const [invitingUser, setInvitingUser] = useState('')
  const [changeRoleData, setChangeRoleData] = useState({
    email: '',
    toRole: '',
  })
  const [adjustPointsData, setAdjustPointsData] = useState(null)
  const [inProgress, setInProgress] = useState(false)
  const [reloading, setReloading] = useState(false)
  const [pointsUpdating, setPointsUpdating] = useState(false)
  const [removeUserInProgress, setRemoveUserInProgress] = useState(false)
  const [updateUserRoleMutation] = useMutation(UPDATE_USER_ROLE)
  const [updateUserPointsMutation] = useMutation(UPDATE_USER_POINTS_MUTATION)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    criteriaMode: 'all',
  })
  const [inviting, setInviting] = useState(false)
  const [createInviteMutation] = useMutation(CREATE_INVITE_MUTATION)
  const [sendInviteEmailMutation] = useMutation(SEND_INVITE_EMAIL_MUTATION)
  const [removeUserMutation] = useMutation(REMOVE_USER)
  const gridRef = useRef()
  const registeredListRef = useRef(null)
  const unRegisteredListRef = useRef(null)
  const inviteListRef = useRef(null)
  const pointsUpdateListRef = useRef(null)
  const fullReportRef = useRef(null)

  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      resizable: true,
    }
  }, [])
  const [
    getInvites,
    { loading: invitesDataLoading, error: invitesDataError, data: invitesData },
  ] = useLazyQuery(GET_INVITES, {
    fetchPolicy: 'no-cache',
  })
  const [getUsers, { loading: usersDataLoading, error: usersDataError }] =
    useLazyQuery(GET_USERS, {
      fetchPolicy: 'no-cache',
    })
  const [getPointsUpdateTransactions] = useLazyQuery(
    GET_POINTS_UPDATE_TRANSACTION_REPORT,
    {
      fetchPolicy: 'no-cache',
    }
  )
  const [getAgendaItems] = useLazyQuery(GET_AGENDA_ITEMS, {
    fetchPolicy: 'no-cache',
  })
  const [getLearningLoungeItems] = useLazyQuery(GET_LEARNING_LOUNGE_ITEMS, {
    fetchPolicy: 'no-cache',
  })

  const setRegistrantRows = useCallback(async (invites = [], users = []) => {
    const registrantList = invites
      .map((invite) => {
        return {
          email: invite.email,
          firstName: '-',
          lastName: '-',
          points: null,
          role: invite.role,
          registered: false,
        }
      })
      .map((account) => {
        let newAccount = Object.assign({}, account)
        const user = users
          .filter((user) => user.email)
          .find((u) => u.email.toUpperCase() === account.email.toUpperCase())

        if (user !== undefined) {
          newAccount.databaseId = user.databaseId
          newAccount.firstName = user.firstName
          newAccount.lastName = user.lastName
          newAccount.points = user.points
          newAccount.registered = true
        }

        return newAccount
      })
      .sort((a, b) => sortStringFunc(a.email, b.email))
    setRows(registrantList)
  }, [])

  /**
   * Refresh the Ag grid list whenever there's a change in rows
   */
  useEffect(() => {
    if (rows.length > 0) {
      if (gridRef.current) {
        gridRef.current.api.refreshCells({ force: true })
      }
    }
  }, [rows])

  /**
   * Reload data
   */
  const reloadData = useCallback(async () => {
    return Promise.all([getInvites(), getUsers()])
      .then((values) => {
        const [invitesQuery, usersQuery] = values
        const invites = invitesQuery?.data?.invites.edges.map(
          (edge) => edge.node
        )
        const users = usersQuery?.data?.users.edges.map((edge) => edge.node)

        setRegistrantRows(invites, users)
      })
      .catch((err) => {
        console.error(`Failed to reload the registrant list`, err)
        toast.error(`Failed to reload the registrant list. Please try again!`)
      })
  }, [getInvites, getUsers, setRegistrantRows])

  /**
   * Reload data when the user visits this page
   */
  useEffect(() => {
    reloadData()
  }, [reloadData])

  /**
   * Handle reload data button click
   */
  const handleReloadClick = async () => {
    setReloading(true)

    reloadData().finally(() => {
      setReloading(false)
    })
  }

  /**
   * Handle change role button click event
   *
   * @param {string} email - User's email address
   * @param {string} toRole - Role to be changed to
   */
  const handleChangeRoleClick = (email = '', toRole = '') => {
    // Save the change role data
    setChangeRoleData({ email, toRole })

    // Open the confirm dialog
    setCdChangeUserRoleOpen(true)
  }

  /**
   * Function to change user's role
   */
  const handleChangeRole = async () => {
    try {
      setInProgress(true)

      await updateUserRoleMutation({
        variables: {
          username: changeRoleData.email,
          role: changeRoleData.toRole,
        },
      })

      // Refresh the list
      reloadData()

      // Close the confirm dialog
      setCdChangeUserRoleOpen(false)
    } catch (err) {
      // Show toast error notification
      toast.error(`Unable to update the user's role. Please try again!`, {
        autoClose: false,
      })
    } finally {
      setInProgress(false)
    }
  }

  const handleAdjustPointDialogClose = () => {
    setAdjustPointsData(null)
  }

  const handleAdjustPoint = async (data, updatePoint, op) => {
    if (!['add', 'sub'].includes(op)) {
      toast.error(
        `Unable to adjust the user's points. Supported operations are ADD or SUB.`
      )
      return
    }

    if (isNaN(updatePoint)) {
      toast.error(`Unable to adjust the user's points. New points not set.`)
      return
    }

    try {
      setPointsUpdating(true)
      const updateUserPointsResponse = await updateUserPointsMutation({
        variables: {
          userId: data?.databaseId,
          operation: op,
          bonus: parseInt(updatePoint),
        },
      })

      // Update success. Close the modal.
      handleAdjustPointDialogClose()

      // Refresh the list to reflect the updated points
      reloadData()

      // Show toast notification
      toast.success(() => (
        <div>
          {[data?.firstName ?? '', data?.lastName ?? ''].join(' ').trim()}'s
          point updated.
          <br />
          New points total:{' '}
          <strong>
            {updateUserPointsResponse?.data.updateUserPoints.pointsAfter ??
              'Unknown'}
          </strong>
        </div>
      ))
    } catch (err) {
      // Show toast error notification
      toast.error(
        `Unable to update the participant's points. Please try again!`
      )
      console.log('Update User Points failed', err)
    } finally {
      setPointsUpdating(false)
    }
  }

  /**
   * Get list
   */
  const getList = async () => {
    const [invitesQuery, usersQuery] = await Promise.all([
      getInvites(),
      getUsers(),
    ])
    const invites = invitesQuery.data.invites.edges.map((invite) => invite.node)
    const users = usersQuery.data.users.edges.map((user) => user.node)
    const list = invites
      .map((invite) => {
        return {
          email: invite.email,
          firstName: '-',
          lastName: '-',
          role: invite.role,
          registered: false,
        }
      })
      .map((account) => {
        let newAccount = Object.assign({}, account)
        const user = users
          .filter((user) => user.email)
          .find((u) => u.email.toLowerCase() === account.email.toLowerCase())

        if (user !== undefined) {
          newAccount.databaseId = user.databaseId
          newAccount.firstName = user.firstName
          newAccount.lastName = user.lastName
          newAccount.registered = true
        }

        return newAccount
      })
      .sort((a, b) => sortStringFunc(a.email, b.email))

    return list
  }

  /**
   * Function to download registered list
   */
  const handleDownloadRegisteredList = async () => {
    const list = await getList()

    const registeredList = list
      .filter((l) => l.registered)
      .map((l) => {
        return [`${l.firstName} ${l.lastName}`, l.email]
      })
    const headers = ['Name', 'Email']

    const contents = [].concat([headers], registeredList)
    const output = makeCSV(contents)

    // Initiate download
    const blob = new Blob([output])
    const downloadUrl = URL.createObjectURL(blob)
    registeredListRef.current.href = downloadUrl
    registeredListRef.current.click()
    URL.revokeObjectURL(downloadUrl)

    // Show toast notification
    toast.success('Registered list downloaded.')
  }

  /**
   * Function to download unregistered list
   */
  const handleDownloadUnRegisteredList = async () => {
    const list = await getList()

    const unRegisteredList = list
      .filter((l) => !l.registered)
      .map((l) => {
        return [l.email]
      })
    const headers = ['Email']

    const contents = [].concat([headers], unRegisteredList)
    const output = makeCSV(contents)

    // Initiate download
    const blob = new Blob([output])
    const downloadUrl = URL.createObjectURL(blob)
    unRegisteredListRef.current.href = downloadUrl
    unRegisteredListRef.current.click()
    URL.revokeObjectURL(downloadUrl)

    // Show toast notification
    toast.success('Unregistered list downloaded.')
  }

  /**
   * Function to download invite list
   */
  const handleDownloadInviteList = async () => {
    const list = await getList()

    const inviteList = list.map((l) => {
      return [
        l.registered ? `${l.firstName} ${l.lastName}` : '',
        l.email,
        l.registered ? 'X' : '',
      ]
    })
    const headers = ['Name', 'Email', 'Registered']

    const contents = [].concat([headers], inviteList)
    const output = makeCSV(contents)

    // Initiate download
    const blob = new Blob([output])
    const downloadUrl = URL.createObjectURL(blob)
    inviteListRef.current.href = downloadUrl
    inviteListRef.current.click()
    URL.revokeObjectURL(downloadUrl)

    // Show toast notification
    toast.success('Invited list downloaded.')
  }

  /**
   * Function to download discretionary point list
   */
  const handleDownloadDiscretionaryPointList = async () => {
    const [usersQuery, pointTransactionsQuery] = await Promise.all([
      getUsers(),
      getPointsUpdateTransactions(),
    ])
    const users = usersQuery.data.users.edges.map((user) => user.node)
    const pointTransactions =
      pointTransactionsQuery.data.pointUpdateTransactionReport
    const contents = generatePointsReport(users, pointTransactions)
    const output = makeCSV(contents)

    // Initiate download
    const blob = new Blob([output])
    const downloadUrl = URL.createObjectURL(blob)
    pointsUpdateListRef.current.href = downloadUrl
    pointsUpdateListRef.current.click()
    URL.revokeObjectURL(downloadUrl)

    // Show toast notification
    toast.success('Points report downloaded.')
  }

  /**
   * Function to download full report
   */
  const handleDownloadFullReport = async () => {
    const [usersQuery, agendaItemsQuery, learningLoungeItemsQuery] =
      await Promise.all([
        getUsers(),
        getAgendaItems(),
        getLearningLoungeItems(),
      ])

    const users = usersQuery.data.users.edges.map((user) => user.node)
    const agendaItems = agendaItemsQuery.data.agendaItems.edges
      .map((agendaItem) => agendaItem.node)
      .sort((a, b) => a.order - b.order)
    const learningLoungeItems =
      learningLoungeItemsQuery.data.learningLoungeItems.edges
        .map((learningLoungeItem) => learningLoungeItem.node)
        .sort((a, b) => a.order - b.order)
    const contents = generateReport(users, agendaItems, learningLoungeItems)
    const output = makeCSV(contents)

    // Initiate download
    const blob = new Blob([output])
    const downloadUrl = URL.createObjectURL(blob)
    fullReportRef.current.href = downloadUrl
    fullReportRef.current.click()
    URL.revokeObjectURL(downloadUrl)

    // Show toast notification
    toast.success('Full report downloaded.')
  }

  /**
   * Function to delete user
   *
   */
  const handleRemoveUser = async () => {
    if (removingUsername === '') {
      toast.error(`Unable to remove the user: Unknown username.`)
      return
    }

    try {
      setRemoveUserInProgress(true)

      await removeUserMutation({
        variables: {
          clientMutationId: uuidv4(),
          username: removingUsername,
        },
      })

      // Refresh the list to reflect the updated list
      reloadData()

      // Remove user succeeded. Clean up and close the modal.
      setRemovingUsername('')
    } catch (err) {
      // Show toast error notification
      toast.error(`Unable to remove the user. Please try again!`, {
        autoClose: false,
      })
    } finally {
      setRemoveUserInProgress(false)
    }
  }

  const columnDefs = useMemo(
    () => [
      {
        field: 'firstName',
        comparator: (a, b) => sortStringFunc(a, b),
        autoHeight: true,
        cellStyle: (params) => {
          if (params.value === '-') {
            return { textAlign: 'center' }
          } else {
            return {}
          }
        },
        cellRenderer: (params) => {
          // Add overlay "remove" button
          return (
            <div className="group relative">
              <div className="absolute left-0 top-1/2 origin-center -translate-y-1/2 scale-0 transition-transform group-hover:scale-100">
                <button
                  className="cursor-pointer rounded-full bg-skin-button-accent px-4 py-2 text-[12px] leading-none text-white transition-colors hover:bg-skin-button-accent-hover active:translate-y-[0.75px] active:scale-[0.99]"
                  onClick={() => setRemovingUsername(params.data.email)}
                >
                  Remove
                </button>
              </div>
              <span>{params.value}</span>
            </div>
          )
        },
      },
      {
        field: 'lastName',
        comparator: (a, b) => sortStringFunc(a, b),
        autoHeight: true,
        cellStyle: (params) => {
          if (params.value === '-') {
            return { textAlign: 'center' }
          } else {
            return {}
          }
        },
      },
      {
        field: 'email',
        comparator: (a, b) => sortStringFunc(a, b),
        autoHeight: true,
        cellStyle: (params) => {
          if (params.value === '-') {
            return { textAlign: 'center' }
          } else {
            return {}
          }
        },
      },
      {
        field: 'points',
        comparator: (a, b) => sortNumberFunc(a, b),
        autoHeight: true,
        cellStyle: (params) => {
          if (params.value === null) {
            return { textAlign: 'center' }
          } else {
            return { lineHeight: 1 }
          }
        },
        cellRenderer: (params) => {
          if (params.value === null) {
            return <div className="text-center">-</div>
          } else {
            return (
              <>
                <div className="mt-1">
                  <button
                    className="mr-1"
                    onClick={() => {
                      setAdjustPointsData({
                        databaseId: params.data.databaseId,
                        firstName: params.data.firstName,
                        lastName: params.data.lastName,
                        email: params.data.email,
                        points: params.data.points,
                        selectedOp: 'add',
                      })
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="3"
                      stroke="currentColor"
                      className="h-3 w-3 text-[#57d974]"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 4.5v15m7.5-7.5h-15"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setAdjustPointsData({
                        databaseId: params.data.databaseId,
                        firstName: params.data.firstName,
                        lastName: params.data.lastName,
                        email: params.data.email,
                        points: params.data.points,
                        selectedOp: 'sub',
                      })
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="3"
                      stroke="currentColor"
                      className="h-3 w-3 text-flame"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 12h-15"
                      />
                    </svg>
                  </button>
                </div>
                <span>{params.value}</span>
              </>
            )
          }
        },
      },
      {
        field: 'role',
        headerName: 'Status',
        autoHeight: true,
        cellStyle: { display: 'flex' },
        cellRenderer: (params) => (
          <div className="flex items-center justify-between">
            <button
              className={clsx(
                'relative mr-4 flex h-[18px] w-[18px] items-center justify-center rounded-full',
                params.value === ROLE_FACILITATOR ? 'bg-flame' : 'bg-dark-gray'
              )}
              onClick={() =>
                handleChangeRoleClick(
                  params.data?.email ?? '',
                  params.data?.role === ROLE_FACILITATOR
                    ? ROLE_PARTICIPANT
                    : params.data?.role === ROLE_PARTICIPANT
                    ? ROLE_FACILITATOR
                    : ''
                )
              }
            >
              <span className="text-[10px] font-bold text-white">
                {params.value.slice(0, 1).toUpperCase()}
              </span>
            </button>
            {params.data.registered ? (
              <CheckIcon className="w-3" />
            ) : (
              <CrossIcon className="w-3" />
            )}
          </div>
        ),
      },
    ],
    []
  )

  // Auto resize columns based on their content
  const onFirstDataRendered = useCallback((e) => {
    let allColumnIds = []

    e.columnApi.getColumns().forEach((column) => {
      if (column.getId() !== 'email') {
        allColumnIds.push(column.getId())
      }
    })
    e.columnApi.autoSizeColumns(allColumnIds)
  }, [])

  const getRowId = useCallback(function (params) {
    return params.data.email
  }, [])

  /**
   * Check if `email` is already in the invite list
   *
   * @param {string} email - User's email address
   */
  const isDuplicateInvite = useCallback(
    (email) => {
      const inviteEmailList =
        invitesData?.invites?.edges.map((edge) =>
          edge.node.email.toLowerCase()
        ) ?? []

      return inviteEmailList.includes(email.toLowerCase())
    },
    [invitesData?.invites?.edges]
  )

  const onInviteFormSubmit = useCallback(
    ({ email }) => {
      if (isDuplicateInvite(email)) {
        setCdInviteUserText(
          `<p class="mb-4"><span class="text-flame">${email}</span> was already invited before.</p><p>Would you like to re-send the invite?</p>`
        )
      } else {
        setCdInviteUserText(
          `Would you like to send an invite to <span class="text-flame">${email}</span>?`
        )
      }

      setCdInviteUserOpen(true)
    },
    [isDuplicateInvite]
  )

  const handleInviteUser = async () => {
    try {
      setCdInviteUserOpen(false)
      setInviting(true)

      // Create invite custom post in the backend
      if (!isDuplicateInvite(invitingUser)) {
        await createInviteMutation({
          variables: {
            clientMutationId: uuidv4(),
            email: invitingUser,
            role: ROLE_PARTICIPANT,
          },
        })
      }

      try {
        // Send an invite email
        await sendInviteEmailMutation({
          variables: {
            to: invitingUser,
            subject: INVITE_SUBJECT,
            body: generateInviteBody(),
          },
        })

        // Show toast notification
        toast.success(`An invite has been sent to ${invitingUser}.`)
      } catch (err) {
        console.error(`Unable to send an invite email.`)
      } finally {
        setInviting(false)

        // Reload the list
        reloadData()
      }
    } catch (err) {
      console.error(`Unable to invite user.`)
    }
  }

  if (invitesDataError || usersDataError) {
    return (
      <div>
        Ooops! An error has occurred. Please make sure your offering is properly
        set up.
      </div>
    )
  }

  return (
    <>
      {/** Confirm dialog for "change user's role" action */}
      <ConfirmDialog
        open={cdChangeUserRoleOpen}
        inProgress={inProgress}
        title="Update user's role"
        text="Are you sure you want to update the role of this user?"
        setOpen={setCdChangeUserRoleOpen}
        onConfirm={handleChangeRole}
        onCancel={() => setCdChangeUserRoleOpen(false)}
      />
      {/** Confirm dialog for "invite additional user" action */}
      <ConfirmDialog
        open={cdInviteUserOpen}
        title="Invite additional participant"
        text={cdInviteUserText}
        setOpen={setCdInviteUserOpen}
        onConfirm={handleInviteUser}
        onCancel={() => setCdInviteUserOpen(false)}
      />
      {/** Confirm dialog for "remove user" action */}
      <ConfirmDialog
        open={removingUsername !== ''}
        title="Remove user"
        text="Removing this user from the offering cannot be undone."
        yesText="Confirm"
        noText="Cancel"
        // setOpen={setRemoveUserOpen}
        inProgress={removeUserInProgress}
        onConfirm={handleRemoveUser}
        onCancel={() => setRemovingUsername('')}
      />
      {/** Confirm dialog for "adjust point" action */}
      <AdjustPointsDialog
        data={adjustPointsData}
        inProgress={pointsUpdating}
        title={`Adjust Points for ${adjustPointsData?.firstName ?? ''} ${
          adjustPointsData?.lastName ?? ''
        }`.trim()}
        setData={setAdjustPointsData}
        onConfirm={handleAdjustPoint}
      />

      <div className="flex justify-between">
        <span>Current Registrants:</span>
        <button
          disabled={invitesDataLoading || usersDataLoading}
          className={clsx(
            'group disabled:cursor-not-allowed',
            reloading && 'animate-spin'
          )}
          onClick={handleReloadClick}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="h-6 w-6 text-skin-accent group-enabled:hover:text-skin-accent-hover"
          >
            <path
              fillRule="evenodd"
              d="M4.755 10.059a7.5 7.5 0 0112.548-3.364l1.903 1.903h-3.183a.75.75 0 100 1.5h4.992a.75.75 0 00.75-.75V4.356a.75.75 0 00-1.5 0v3.18l-1.9-1.9A9 9 0 003.306 9.67a.75.75 0 101.45.388zm15.408 3.352a.75.75 0 00-.919.53 7.5 7.5 0 01-12.548 3.364l-1.902-1.903h3.183a.75.75 0 000-1.5H2.984a.75.75 0 00-.75.75v4.992a.75.75 0 001.5 0v-3.18l1.9 1.9a9 9 0 0015.059-4.035.75.75 0 00-.53-.918z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {/** List */}
      <div className="ag-theme-alpine mt-2 h-[350px]">
        <AgGridReact
          ref={gridRef}
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows={true}
          getRowId={getRowId}
          onFirstDataRendered={onFirstDataRendered}
          // onRowDataUpdated={onRowDataUpdated}
        ></AgGridReact>
      </div>

      {/** Status report */}
      <div className="mt-1 flex justify-between">
        <span>
          Registered:{' '}
          <button
            type="button"
            disabled={invitesDataLoading || usersDataLoading}
            className="text-flame enabled:hover:underline disabled:cursor-not-allowed"
            onClick={handleDownloadRegisteredList}
          >
            {rows.length > 0
              ? rows.filter((row) => row.registered).length
              : '-'}
          </button>
          <a
            className="hidden"
            download={`TMC-Registered-list_${format(
              new Date(),
              'yyyy-LL-dd_HHmmss'
            )}.csv`}
            href=" "
            ref={registeredListRef}
          >
            download registered list
          </a>
        </span>
        <span>|</span>
        <span>
          Unregistered:{' '}
          <button
            type="button"
            disabled={invitesDataLoading || usersDataLoading}
            className="text-flame enabled:hover:underline disabled:cursor-not-allowed"
            onClick={handleDownloadUnRegisteredList}
          >
            {rows.length > 0
              ? rows.filter((row) => !row.registered).length
              : '-'}
          </button>
          <a
            className="hidden"
            download={`TMC-Unregistered-list_${format(
              new Date(),
              'yyyy-LL-dd_HHmmss'
            )}.csv`}
            href=" "
            ref={unRegisteredListRef}
          >
            download unregistered list
          </a>
        </span>
        <span>|</span>
        <span>
          Invited:{' '}
          <button
            type="button"
            disabled={invitesDataLoading || usersDataLoading}
            className="text-flame enabled:hover:underline disabled:cursor-not-allowed"
            onClick={handleDownloadInviteList}
          >
            {invitesData?.invites.edges.length > 0
              ? invitesData?.invites.edges.length
              : '-'}
          </button>
          <a
            className="hidden"
            download={`TMC-Invited-list_${format(
              new Date(),
              'yyyy-LL-dd_HHmmss'
            )}.csv`}
            href=" "
            ref={inviteListRef}
          >
            download invite list
          </a>
        </span>
        <span>|</span>
        <div>
          <button
            type="button"
            disabled={invitesDataLoading || usersDataLoading}
            className="text-flame enabled:hover:underline disabled:cursor-not-allowed"
            onClick={handleDownloadDiscretionaryPointList}
          >
            Discretionary Points Report
          </button>
          <a
            className="hidden"
            download={`TMC-Discretionary-Points-Report_${format(
              new Date(),
              'yyyy-LL-dd_HHmmss'
            )}.csv`}
            href=" "
            ref={pointsUpdateListRef}
          >
            download discretionary points report
          </a>
        </div>
        <span>|</span>
        <div>
          <button
            type="button"
            disabled={invitesDataLoading || usersDataLoading}
            className="text-flame enabled:hover:underline disabled:cursor-not-allowed"
            onClick={handleDownloadFullReport}
          >
            Full Report
          </button>
          <a
            className="hidden"
            download={`TMC-Report_${format(
              new Date(),
              'yyyy-LL-dd_HHmmss'
            )}.csv`}
            href=" "
            ref={fullReportRef}
          >
            download full report
          </a>
        </div>
      </div>

      {/** Invite additional participant */}
      <div>
        <p className="mt-10 font-bold text-flame">
          Need to invite an additional participant?
        </p>
        <p>
          Enter their email below (one at a time) and click "invite". This will
          send them an invitation email and prompt them to register.
        </p>
        <div className="mt-10 text-center">
          <form
            onSubmit={handleSubmit(onInviteFormSubmit)}
            className={clsx('group', inviting && 'inviting')}
          >
            <input
              {...register('email', { required: true })}
              name="email"
              type="email"
              placeholder="enter email here"
              value={invitingUser}
              disabled={inviting}
              className={clsx(
                'mb-4 mr-4 w-full rounded-[6.5px] border-light-gray placeholder-dark-gray sm:mb-0 sm:w-[250px]',
                errors.email ? 'border-red-500' : 'border-light-gray'
              )}
              onChange={(e) => setInvitingUser(e.target.value)}
            />
            <input
              type="submit"
              value={inviting ? 'Inviting...' : 'Invite'}
              disabled={inviting}
              className="cursor-pointer rounded-full bg-skin-button-accent px-8 pb-3 pt-[0.6rem] text-white transition-colors group-[.inviting]:cursor-not-allowed group-[.inviting]:opacity-50 group-[:not(.inviting)]:hover:bg-skin-button-accent-hover group-[:not(.inviting)]:active:translate-y-[0.75px] group-[:not(.inviting)]:active:scale-[0.99]"
            />
          </form>
        </div>
      </div>
    </>
  )
}

export default AdminViewRegistrants
